/**
 * Dataset oficial de los 6 módulos de inducción, universidades, carreras,
 * actividades y textos regulatorios de ABAQ Querétaro.
 * Réplica idéntica y tipada del mockup de React (spec-5, spec-6, spec-9, spec-11, spec-14).
 */

export interface InductionQuestion {
  key: 'liked' | 'important' | 'improve';
  label: string;
  placeholder: string;
}

export interface SocialChannel {
  id: string;
  name: string;
  handle: string;
  url: string;
  icon: string;
  actionText: string;
}

export interface InductionModule {
  id: number;
  targetId: string;
  number: string;
  title: string;
  badge?: string;
  badgeIcon?: string;
  description?: string;
  type: 'social' | 'canva' | 'youtube';
  mediaUrl?: string;
  embedId?: string;
  actionText?: string;
  socialChannels?: SocialChannel[];
  confirmationCheckboxText?: string;
  previewCard?: {
    title: string;
    description: string;
    buttonText: string;
    icon: string;
  };
  calloutBox?: {
    icon: string;
    kicker: string;
    highlight: string;
    text: string;
  };
  questions: InductionQuestion[];
}

export const INDUCTION_MODULES: InductionModule[] = [
  {
    id: 1,
    targetId: 'conociendo-abaq',
    number: '1',
    title: '1. Conociendo ABAQ',
    badge: 'Web & Redes',
    badgeIcon: 'public',
    description:
      'Visita nuestra página web oficial y síguenos en nuestras redes sociales oficiales para validar este punto.',
    type: 'social',
    socialChannels: [
      {
        id: 'web',
        name: 'Página Web',
        handle: 'abaq.org.mx',
        url: 'https://www.abaq.org.mx',
        icon: 'language',
        actionText: 'Visitar ↗',
      },
      {
        id: 'facebook',
        name: 'Facebook',
        handle: '@ABAQ.qro',
        url: 'https://facebook.com/ABAQ.qro',
        icon: 'thumb_up',
        actionText: 'Seguir ↗',
      },
      {
        id: 'instagram',
        name: 'Instagram',
        handle: '@abaqamorybienestar',
        url: 'https://instagram.com/abaqamorybienestar/',
        icon: 'photo_camera',
        actionText: 'Seguir ↗',
      },
      {
        id: 'linkedin',
        name: 'LinkedIn',
        handle: 'ABAQ Querétaro',
        url: 'https://linkedin.com/in/abaq-quer%C3%A9taro-51a97325b/',
        icon: 'work',
        actionText: 'Conectar ↗',
      },
      {
        id: 'tiktok',
        name: 'TikTok',
        handle: '@abaq2023',
        url: 'https://tiktok.com/@abaq2023',
        icon: 'music_video',
        actionText: 'Seguir ↗',
      },
      {
        id: 'twitter',
        name: 'Twitter / X',
        handle: '@AbaqQueretaro',
        url: 'https://twitter.com/AbaqQueretaro',
        icon: 'tag',
        actionText: 'Seguir ↗',
      },
    ],
    confirmationCheckboxText: 'He visitado la web y seguido las redes oficiales de ABAQ.',
    questions: [
      {
        key: 'liked',
        label: '¿Qué me gustó?',
        placeholder: 'Ej. La claridad en la misión de rescate animal en Querétaro y la presencia en múltiples plataformas...',
      },
      {
        key: 'important',
        label: '¿Qué considero lo más importante?',
        placeholder: 'Ej. El impacto comunitario directo y la seriedad del servicio social...',
      },
      {
        key: 'improve',
        label: '¿Qué puede mejorar?',
        placeholder: 'Ej. Podrían agregar más infografías de adopción responsable en Instagram...',
      },
    ],
  },
  {
    id: 2,
    targetId: 'bienestar-animal',
    number: '2',
    title: '2. Plática de Bienestar Animal ABAQ',
    type: 'canva',
    mediaUrl: 'https://www.canva.com/design/DAGNqq8CewM/9Iv7Hby2Bgi2egNilw1Szw/watch',
    actionText: 'Ver plática en Canva',
    previewCard: {
      title: 'Presentación Oficial de Bienestar Animal',
      description: 'Conoce los principios éticos, protocolos veterinarios y enfoque de cuidado libre de maltrato.',
      buttonText: 'Abrir Canva ↗',
      icon: 'pets',
    },
    questions: [
      {
        key: 'liked',
        label: '¿Qué me gustó?',
        placeholder: 'Ej. Los protocolos éticos de bienestar y la explicación veterinaria de esterilización...',
      },
      {
        key: 'important',
        label: '¿Qué considero lo más importante?',
        placeholder: 'Ej. El enfoque de cuidado libre de maltrato y las 5 libertades del bienestar animal...',
      },
      {
        key: 'improve',
        label: '¿Qué puede mejorar?',
        placeholder: 'Ej. Incluir ejemplos específicos de casos de rescate atendidos en Querétaro...',
      },
    ],
  },
  {
    id: 3,
    targetId: 'arquetipos-marca',
    number: '3',
    title: '3. Arquetipos de marca',
    type: 'youtube',
    mediaUrl: 'https://www.youtube.com/watch?v=BtJnW7z0pXU',
    embedId: 'BtJnW7z0pXU',
    actionText: 'Ver video en YouTube',
    calloutBox: {
      icon: 'auto_awesome',
      kicker: 'NOTA INSTITUCIONAL',
      highlight: 'Arquetipo elegido por ABAQ: El Inocente.',
      text: ' Nuestro tono y comunicación reflejan esperanza, pureza, optimismo y protección incondicional.',
    },
    questions: [
      {
        key: 'liked',
        label: '¿Qué me gustó?',
        placeholder: 'Ej. La profundidad de la teoría de arquetipos de Carl Jung aplicada a causas sociales...',
      },
      {
        key: 'important',
        label: '¿Qué considero lo más importante?',
        placeholder: 'Ej. Que el arquetipo de ABAQ sea "El Inocente", transmitiendo pureza, optimismo y protección...',
      },
      {
        key: 'improve',
        label: '¿Qué puede mejorar?',
        placeholder: 'Ej. Dar guías de redacción para voluntarias y voluntarios en brigadas...',
      },
    ],
  },
  {
    id: 4,
    targetId: 'entrenamiento-disc',
    number: '4',
    title: '4. Entrenamiento DISC',
    type: 'youtube',
    mediaUrl: 'https://www.youtube.com/watch?v=CKkp8phM7N8',
    embedId: 'CKkp8phM7N8',
    actionText: 'Ver entrenamiento DISC',
    description:
      'Comprende los diferentes estilos de personalidad y comunicación (Dominancia, Influencia, Serenidad, Cumplimiento) para un trabajo voluntario armónico y coordinado.',
    questions: [
      {
        key: 'liked',
        label: '¿Qué me gustó?',
        placeholder: 'Ej. El test y desglose de perfiles: Dominancia, Influencia, Serenidad y Cumplimiento...',
      },
      {
        key: 'important',
        label: '¿Qué considero lo más importante?',
        placeholder: 'Ej. Aprender a coordinar brigadas combinando diferentes estilos de comunicación...',
      },
      {
        key: 'improve',
        label: '¿Qué puede mejorar?',
        placeholder: 'Ej. Un cuestionario interactivo más breve para autoevaluación...',
      },
    ],
  },
  {
    id: 5,
    targetId: 'entrenamiento-generaciones',
    number: '5',
    title: '5. Entrenamiento Generaciones',
    type: 'youtube',
    mediaUrl: 'https://www.youtube.com/watch?v=QK-fUjSb_Vo',
    embedId: 'QK-fUjSb_Vo',
    actionText: 'Ver entrenamiento Generaciones',
    description:
      'Identifica los puentes de empatía y trabajo conjunto entre Baby Boomers, Generación X, Millennials y Generación Z dentro de proyectos comunitarios.',
    questions: [
      {
        key: 'liked',
        label: '¿Qué me gustó?',
        placeholder: 'Ej. El respeto y comprensión mutua entre generaciones distintas...',
      },
      {
        key: 'important',
        label: '¿Qué considero lo más importante?',
        placeholder: 'Ej. El valor de la diversidad generacional en ABAQ...',
      },
      {
        key: 'improve',
        label: '¿Qué puede mejorar?',
        placeholder: 'Ej. Casos prácticos de trabajo en equipo intergeneracional en refugios...',
      },
    ],
  },
  {
    id: 6,
    targetId: 'neurociencia',
    number: '6',
    title: '6. La neurociencia de las emociones (Marian Rojas Estapé)',
    type: 'youtube',
    mediaUrl: 'https://www.youtube.com/watch?v=TjqrualxgkI&t=584s',
    embedId: 'TjqrualxgkI',
    actionText: 'Ver video en YouTube',
    description:
      'Aprende cómo gestionar el cortisol, la empatía y la resiliencia emocional durante situaciones de rescate y servicio social.',
    questions: [
      {
        key: 'liked',
        label: '¿Qué me gustó?',
        placeholder: 'Ej. Las explicaciones médicas y neurológicas de la Dra. Marian Rojas Estapé sobre el cortisol...',
      },
      {
        key: 'important',
        label: '¿Qué considero lo más importante?',
        placeholder: 'Ej. Gestionar las emociones y mantener la resiliencia en rescates difíciles...',
      },
      {
        key: 'improve',
        label: '¿Qué puede mejorar?',
        placeholder: 'Ej. Técnicas de respiración guiada aplicadas al voluntariado en campo...',
      },
    ],
  },
];

export const QUERETARO_UNIVERSITIES = [
  'Universidad Autónoma de Querétaro (UAQ)',
  'Instituto Tecnológico de Querétaro (ITQ)',
  'Universidad Tecnológica de Querétaro (UTEQ)',
  'Universidad Anáhuac Querétaro',
  'Universidad del Valle de México (UVM Querétaro)',
  'Universidad Politécnica de Querétaro (UPQ)',
  'Universidad Aeronáutica en Querétaro (UNAQ)',
  'Escuela Normal del Estado de Querétaro (ENEQ)',
  'Tecnológico de Monterrey Campus Querétaro (ITESM)',
];

export const CAREER_SUGGESTIONS = [
  'Licenciatura en Psicología',
  'Medicina Veterinaria y Zootecnia',
  'Ingeniería de Software / Sistemas',
  'Licenciatura en Biología',
  'Ciencias de la Comunicación',
  'Diseño y Comunicación Visual',
  'Licenciatura en Derecho',
  'Administración de Empresas',
  'Ingeniería Ambiental',
];

export interface AccreditableActivity {
  id: number;
  activity: string;
  hours: string;
}

export const OFFICIAL_ACCREDITABLE_ACTIVITIES: AccreditableActivity[] = [
  {
    id: 1,
    activity:
      'Eventos ABAQ: pláticas de bienestar animal, campañas de esterilización, vacunación, desparasitación, identificación, marchas, visitas, etc. Incluye apoyo en refugios, etc. con alianza ABAQ.',
    hours: '2x1 (doble) por cada hora de participación',
  },
  {
    id: 2,
    activity:
      'Vincular que alguien le ponga placa de identificación a su perro o gato mediante el proveedor ABAQ.',
    hours: '5 horas',
  },
  {
    id: 3,
    activity:
      'Vincular que alguien esterilice a su perro o gato, haciendo el interesado la inscripción con ABAQ.',
    hours: '10 horas',
  },
  {
    id: 4,
    activity:
      'Vincular adopción de un perro o gato de colaboración ABAQ (Adopcan: perros con Sergio Medina; Adopta Michu: gatos con Cristina Pedraza), cumpliendo el protocolo ABAQ de SS, pasando los 15 días del periodo de prueba.',
    hours: '100 horas',
  },
  {
    id: 5,
    activity:
      'Vincular y coordinar para que ABAQ instale un módulo de adopción en algún evento o lugar público.',
    hours: '40 horas',
  },
  {
    id: 6,
    activity:
      'Vincular y coordinar para que ABAQ imparta —presencial y/o virtual— la plática "Bienestar Animal: Control poblacional de perros y gatos" en escuelas, empresas y/o colonias, de mínimo 50 participantes.',
    hours: '50 horas',
  },
  {
    id: 7,
    activity:
      'Vincular y coordinar para que ABAQ realice una campaña a bajo costo de esterilización, adopción, identificación y/o vacunación y desparasitación en escuelas, empresas y/o colonias, de mínimo 60 perros y/o gatos inscritos, contando con el espacio para instalación de quirófano, área de recuperación, baños, luz, agua, espera, etc.',
    hours: '100 horas',
  },
  {
    id: 8,
    activity:
      'Vincular y coordinar para que ABAQ difunda y/o publique el proyecto ABAQ gratuitamente en algún medio de comunicación: radio, TV, podcast, periódico, revista, panorámico, etc.',
    hours: '30 horas',
  },
  {
    id: 9,
    activity:
      'Realizar publicaciones/post en redes sociales, previamente aprobadas por ABAQ: republicaciones y repost de las publicaciones ABAQ en sus redes sociales, etiquetando a ABAQ, de tal forma que quede también en las redes de ABAQ.',
    hours: '5 horas + 1 hora por cada 5 reacciones',
  },
  {
    id: 10,
    activity:
      'Conseguir fondos para el cumplimiento del objeto social ABAQ (esterilización, vacunas, alimento, curaciones, insumos).',
    hours: '1 hora: $50.00',
  },
  {
    id: 11,
    activity:
      'Otras actividades administrativas eventualmente, como creación de contenido, archivo digital o físico, etc., que serán convocadas en el mismo grupo de WA.',
    hours: 'Por definir de acuerdo con la actividad; se notificará a la hora de la convocatoria',
  },
];

export const DEFAULT_PRIORITIZED_ACTIVITIES = [
  'Eventos ABAQ: pláticas de bienestar animal, campañas de esterilización, vacunación, desparasitación, identificación, marchas, visitas, etc. Incluye apoyo en refugios, etc. con alianza ABAQ. (Acredita: 2x1 doble por cada hora de participación)',
  'Vincular que alguien le ponga placa de identificación a su perro o gato mediante el proveedor ABAQ. (Acredita: 5 horas)',
  'Vincular que alguien esterilice a su perro o gato, haciendo el interesado la inscripción con ABAQ. (Acredita: 10 horas)',
  'Vincular adopción de un perro o gato de colaboración ABAQ (Adopcan: perros con Sergio Medina; Adopta Michu: gatos con Cristina Pedraza), cumpliendo el protocolo ABAQ de SS, pasando los 15 días del periodo de prueba. (Acredita: 100 horas)',
  'Vincular y coordinar para que ABAQ instale un módulo de adopción en algún evento o lugar público. (Acredita: 40 horas)',
  'Vincular y coordinar para que ABAQ imparta —presencial y/o virtual— la plática "Bienestar Animal: Control poblacional de perros y gatos" en escuelas, empresas y/o colonias, de mínimo 50 participantes. (Acredita: 50 horas)',
  'Vincular y coordinar para que ABAQ realice una campaña a bajo costo de esterilización, adopción, identificación y/o vacunación y desparasitación en escuelas, empresas y/o colonias, de mínimo 60 perros y/o gatos inscritos, contando con el espacio para instalación de quirófano, área de recuperación, baños, luz, agua, espera, etc. (Acredita: 100 horas)',
  'Vincular y coordinar para que ABAQ difunda y/o publique el proyecto ABAQ gratuitamente en algún medio de comunicación: radio, TV, podcast, periódico, revista, panorámico, etc. (Acredita: 30 horas)',
  'Realizar publicaciones/post en redes sociales, previamente aprobadas por ABAQ: republicaciones y repost de las publicaciones ABAQ en sus redes sociales, etiquetando a ABAQ, de tal forma que quede también en las redes de ABAQ. (Acredita: 5 horas + 1 hora por cada 5 reacciones)',
  'Conseguir fondos para el cumplimiento del objeto social ABAQ (esterilización, vacunas, curaciones, material de limpieza, alimento, gastos operativos). (Acredita: 1 hora: $50.00)',
  'Otras actividades administrativas eventualmente, como creación de contenido, archivo digital o físico, etc., que serán convocadas en el mismo grupo de WA. (Horas: Por definir de acuerdo con la actividad; se notificará a la hora de la convocatoria)',
];

export const QUICK_INTEREST_TAGS = [
  'Educación ambiental',
  'Redes sociales',
  'Logística',
  'Rescate animal',
  'Jornadas de esterilización',
  'Ferias de adopción',
  'Fotografía de naturaleza',
  'Primeros auxilios veterinarios',
];

export const REGULATORY_DOCUMENTS_TEXT = {
  reglamento: {
    id: 'reglamento',
    title: 'Reglamento de Servicio Social ABAQ Querétaro',
    content: `1. OBJETO Y ALCANCE
El presente Reglamento establece los lineamientos, derechos y obligaciones para estudiantes universitarios y voluntarios que realizan actividades en la Asociación de Beneficencia y Adopción de Querétaro (ABAQ Querétaro).

2. ACTIVIDADES DISPONIBLES Y HORAS ACREDITABLES
1. Eventos ABAQ: pláticas, esterilización, vacunación, desparasitación, identificación, marchas y apoyo en refugios: 2x1 (doble) por cada hora de participación.
2. Vincular que alguien le ponga placa de identificación mediante el proveedor ABAQ: 5 horas.
3. Vincular esterilización de perro o gato registrándose con ABAQ: 10 horas.
4. Vincular adopción de perro o gato en colaboración ABAQ (Adopcan / Adopta Michu) con 15 días de prueba: 100 horas.
5. Vincular y coordinar instalación de módulo de adopción en evento o lugar público: 40 horas.
6. Vincular y coordinar plática de Bienestar Animal (mínimo 50 participantes): 50 horas.
7. Vincular y coordinar campaña a bajo costo (esterilización, vacunación, etc., mínimo 60 animales): 100 horas.
8. Vincular y coordinar difusión gratuita en medios (radio, TV, podcast, prensa, panorámicos): 30 horas.
9. Publicaciones y reposts en redes sociales etiquetando a ABAQ: 5 horas + 1 hora por cada 5 reacciones.
10. Conseguir fondos para el objeto social ABAQ: 1 hora por cada $50.00.
11. Actividades administrativas (creación de contenido, archivo digital o físico en grupo de WA): Horas por definir.

3. REGLAS ESPECÍFICAS
- NOTA: En caso de Servicio Social, esta presentación será válida por 10 horas, además de que te permitirá continuar. La acreditación de horas aplica exclusivamente para voluntarios de Servicio Social estudiantil (el voluntariado general no acredita horas escolares).
- Ningún estudiante podrá superar el tope de horas estipulado en su carta de aceptación (máximo 480 horas).
- Anáhuac: solo se acreditará hasta el 50% de las horas en la actividad 10 (consecución de fondos).
- ITESM y Tec Milenio: no aplica la actividad 10.

4. CUIDADO Y RESPETO ANIMAL
El trato hacia los animales rescatados se rige bajo los principios de bienestar animal, evitando cualquier acto de estrés innecesario, maltrato o negligencia.

5. CONTINUIDAD Y REGLA DE DOS MESES
Para estudiantes de servicio social: 2 meses de inactividad no previamente notificada causará la baja automática del programa.`,
  },
  privacidad: {
    id: 'privacidad',
    title: 'Aviso de Privacidad y Manejo de Datos Personales',
    content: `En cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, ABAQ Querétaro con domicilio en Santiago de Querétaro, Qro., informa que los datos personales, académicos y de contacto recabados serán utilizados exclusivamente para:
a) Registro y seguimiento del expediente institucional.
b) Emisión de cartas oficiales de aceptación, acreditación y término.
c) Contacto institucional y asignación de brigadas de rescate y esterilización.
Tus datos no serán comercializados ni transferidos a terceros ajenos a las instituciones vinculadas.`,
  },
  etica: {
    id: 'etica',
    title: 'Políticas de Servicio y Código de Ética Institucional',
    content: `1. COMPROMISO Y PUNTUALIDAD
Los participantes deberán asistir puntualmente a las jornadas inscritas y notificar con anticipación en caso de fuerza mayor.

2. COMUNICACIÓN RESPETUOSA
Se promoverá la empatía intergeneracional, el trabajo colaborativo DISC y la protección incondicional inspirada en nuestro arquetipo "El Inocente".

3. USO DE REDES SOCIALES
Toda difusión de rescates y eventos de adopción deberá respetar la dignidad de los animales y proyectar esperanza y optimismo institucional.`,
  },
  terminos: {
    id: 'terminos',
    title: 'Términos y Condiciones de la Plataforma Digital ABAQ',
    content: `El acceso a la plataforma digital de ABAQ está sujeto a las siguientes condiciones:
- Los enlaces de documentos en la nube deben mantenerse públicos y con vigencia mínima de un mes.
- La formalización digital mediante casillas de consentimiento sustituye formalmente la firma autógrafa.
- El comité de coordinación se reserva el derecho de auditar y desestimar horas reportadas con inconsistencias.`,
  },
};
